import React, { useState } from "react";
import {
  Calendar,
  BarChart3,
  Clock,
  Tag,
  TrendingUp,
  PieChart,
  FileText,
} from "lucide-react";
import type {
  CampaignReportResponse,
  LinearReportResponse,
} from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { DonutChart } from "../../components/DonutChart";
import { BarChart } from "../../components/BarChart";
import { ClientLogo } from "../../components/ClientLogo";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { CARD_BG, PAGE_BG, SHADOW } from "../../config/theme";
import { LinearLayout } from "../linear";
import { AiInsightsSection } from "../../components/AiInsightsSection";
import {
  buildOriginalLayoutViewModel,
  type OriginalRoasTone,
} from "./originalLayoutViewModel";

interface OriginalLayoutProps {
  data: CampaignReportResponse | LinearReportResponse;
  reportId?: string;
  onNewUpload?: () => void;
  clientId?: string;
}

// ==================== BACKGROUND COLOR CONFIGURATION ====================
// Toggle between white and gray backgrounds
// Set to true for white background, false for gray background
// ========================================================================
// Theme configuration imported from central config
// To change card background colors, edit: frontend/src/config/theme.ts
// Options: 'slate' | 'stone' | 'neutral' | 'gray'
const BACKGROUND_CLASSES = PAGE_BG;
const CARD_BACKGROUND_CLASSES = CARD_BG;
// ========================================================================

const roasBadgeClasses = (tone: OriginalRoasTone): string => {
  if (tone === "excellent") {
    return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
  }
  if (tone === "good") {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
  }
  return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
};

/**
 * Original continuous scroll layout optimized for web viewing.
 * This is the pre-PowerPoint design with all sections in a single page.
 */
export const OriginalLayout: React.FC<OriginalLayoutProps> = ({
  data,
  reportId,
  clientId,
}) => {
  // Linear reports use a dedicated layout component
  if (data.report_type === "linear") {
    return (
      <LinearLayout
        data={data as LinearReportResponse}
        reportId={reportId}
        clientId={clientId}
      />
    );
  }

  // Type assertion for non-linear reports
  const campaignData = data as CampaignReportResponse;
  const viewModel = buildOriginalLayoutViewModel(campaignData);

  // Client ID from saved report metadata
  const [hiddenSections, setHiddenSections] = useState<Record<string, boolean>>(
    {},
  );

  const hideSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: true }));

  const restoreSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: false }));

  const isSectionHidden = (sectionId: string) =>
    Boolean(hiddenSections[sectionId]);

  const resolvedClientId = clientId ?? "ajinomoto";

  return (
    <div className={`py-16 px-8 ${BACKGROUND_CLASSES}`} data-dashboard>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Campaign Performance Report: Walmart
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {viewModel.reportBadge}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {viewModel.dateRangeLabel}
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  {viewModel.recordsProcessedLabel}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {viewModel.daysLabel}
                </span>
              </div>
            </div>
            <div>
                  <ClientLogo
                    clientId={resolvedClientId}
                className="h-16 object-contain"
                fallback={
                  <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
                }
              />
            </div>
          </div>
        </div>

        <AiInsightsSection data={campaignData} reportId={reportId} />

        {/* Key Metrics Summary */}
        <SectionToggleWrapper
          sectionId="original-overall-performance"
          title="Overall Performance"
          hidden={isSectionHidden("original-overall-performance")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Overall Performance
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title={viewModel.overallMetrics.totalSales.title}
              value={viewModel.overallMetrics.totalSales.value}
              subtitle={viewModel.overallMetrics.totalSales.subtitle}
              trend={viewModel.overallMetrics.totalSales.trend}
            />
            <MetricCard
              title={viewModel.overallMetrics.avgDailySales.title}
              value={viewModel.overallMetrics.avgDailySales.value}
              subtitle={viewModel.overallMetrics.avgDailySales.subtitle}
              trend={viewModel.overallMetrics.avgDailySales.trend}
            />
            <MetricCard
              title={viewModel.overallMetrics.totalSpend.title}
              value={viewModel.overallMetrics.totalSpend.value}
              subtitle={viewModel.overallMetrics.totalSpend.subtitle}
              trend={viewModel.overallMetrics.totalSpend.trend}
            />
            <MetricCard
              title={viewModel.overallMetrics.roas.title}
              value={viewModel.overallMetrics.roas.value}
              subtitle={viewModel.overallMetrics.roas.subtitle}
              trend={viewModel.overallMetrics.roas.trend}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <MetricCard
              title={viewModel.overallMetrics.impressions.title}
              value={viewModel.overallMetrics.impressions.value}
              subtitle={viewModel.overallMetrics.impressions.subtitle}
              trend={viewModel.overallMetrics.impressions.trend}
            />
            <MetricCard
              title={viewModel.overallMetrics.totalClicks.title}
              value={viewModel.overallMetrics.totalClicks.value}
              subtitle={viewModel.overallMetrics.totalClicks.subtitle}
              trend={viewModel.overallMetrics.totalClicks.trend}
            />
            <MetricCard
              title={viewModel.overallMetrics.clickRate.title}
              value={viewModel.overallMetrics.clickRate.value}
              subtitle={viewModel.overallMetrics.clickRate.subtitle}
              trend={viewModel.overallMetrics.clickRate.trend}
            />
            <MetricCard
              title={viewModel.overallMetrics.ntbSalesShare.title}
              value={viewModel.overallMetrics.ntbSalesShare.value}
              subtitle={viewModel.overallMetrics.ntbSalesShare.subtitle}
              trend={viewModel.overallMetrics.ntbSalesShare.trend}
            />
          </div>
        </SectionToggleWrapper>

        {/* Brand Performance Summary */}
        <SectionToggleWrapper
          sectionId="original-brand-performance"
          title="Performance by Brand"
          hidden={isSectionHidden("original-brand-performance")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Performance by Brand
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {viewModel.brandCards.map((brand) => (
              <div
                key={brand.brand}
                className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {brand.brand}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roasBadgeClasses(brand.roasTone)}`}
                  >
                    {brand.roasValue.toFixed(2)}x ROAS
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Sales
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      ${brand.totalSalesDisplay}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Spend
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      ${brand.totalSpendDisplay}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Units
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {brand.total_units.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      CTR
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {brand.ctrPercentage.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Impressions
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {brand.totalImpressionsDisplay}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionToggleWrapper>

        {/* Charts Section */}
        <SectionToggleWrapper
          sectionId="original-visual-analysis"
          title="Visual Analysis"
          hidden={isSectionHidden("original-visual-analysis")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Visual Analysis
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales Distribution */}
            <div
              className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Sales Distribution by Brand
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {viewModel.salesDistributionSubtitle}
              </p>
              <DonutChart data={viewModel.brandSalesData} />
            </div>

            {/* ROAS Comparison */}
            <div
              className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Return on Ad Spend by Brand
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {viewModel.roasTargetSubtitle}
              </p>
              <BarChart
                labels={viewModel.roasChart.labels}
                datasets={[
                  {
                    label: "ROAS",
                    data: viewModel.roasChart.values,
                    backgroundColor: viewModel.roasChart.colors,
                  },
                ]}
                horizontal={true}
                showLegend={false}
              />
            </div>
          </div>
        </SectionToggleWrapper>

        {/* Daily Performance Trend */}
        <SectionToggleWrapper
          sectionId="original-daily-trend"
          title="Daily Performance Trend"
          hidden={isSectionHidden("original-daily-trend")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Daily Performance Trend
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {viewModel.dailyTrendSubtitle}
            </p>
            <BarChart
              labels={viewModel.dailyTrendChart.labels}
              datasets={viewModel.dailyTrendChart.datasets}
              stacked={false}
              horizontal={false}
              showLegend={true}
              height="h-96"
            />
          </div>
        </SectionToggleWrapper>

        {/* Campaign Details Table */}
        <SectionToggleWrapper
          sectionId="original-campaign-details"
          title="Campaign Details"
          hidden={isSectionHidden("original-campaign-details")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Campaign Details
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {viewModel.campaignDetailsSubtitle}
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-[#403838]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Sales
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Spend
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      ROAS
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Units
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                      Days
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${CARD_BACKGROUND_CLASSES} divide-y divide-gray-200 dark:divide-gray-700`}
                >
                  {viewModel.campaignRows.map((campaign) => (
                    <tr
                      key={campaign.campaign_name}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td
                        className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate"
                        title={campaign.campaign_name}
                      >
                        {campaign.campaign_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {campaign.brand}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {campaign.product}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                        ${campaign.totalSalesDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                        ${campaign.totalSpendDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roasBadgeClasses(campaign.roasTone)}`}
                        >
                          {campaign.roasValue.toFixed(2)}x
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                        {campaign.total_units.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                        {campaign.days_active}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SectionToggleWrapper>
      </div>
    </div>
  );
};
